import { FieldValues } from 'react-hook-form';
import { DatePickerMobileSelectorProps } from './props';
import { motion } from 'framer-motion';
import { slideIn } from './variants';
import styles from './styles.module.scss';
import { useClickOutside, useCustomDatePicker } from '@starsoft/common/hooks';
import { ModalHeader } from '@starsoft/common/components';
import DatePickerMobileSelectorColumn from './Column';

/**
 * Mobile date picker selector component that displays a modal with scrollable columns for day, month and year selection
 * Uses framer-motion for animations and handles click outside to close
 *
 * @component
 * @template T - Generic type extending FieldValues for form handling
 * @param {Object} props - Component props
 * @param {Function} props.toggleOpen - Function to toggle the modal open/closed
 * @param {string} props.placeholder - Placeholder text to show in modal header
 * @param {boolean} props.isLoading - Whether the component is in loading state
 * @param {string|Date} props.value - Currently selected date value
 * @param {Function} props.onChange - Callback when date selection changes
 * @param {boolean} props.showSpinner - Whether to show loading spinner
 * @returns {JSX.Element} Mobile date picker selector modal with scrollable columns
 */
export default function DatePickerMobileSelector<T extends FieldValues>({
  toggleOpen,
  placeholder,
  isLoading,
  value,
  ...props
}: DatePickerMobileSelectorProps<T>) {
  // Get date picker state and update handlers from custom hook
  const {
    days,
    years,
    months,
    updateDay,
    updateMonth,
    updateYear,
    current: { day, month, year },
  } = useCustomDatePicker({
    date: new Date((value as string) ?? new Date()).toString(),
    callback: props?.onChange,
    isLoading: props?.showSpinner || isLoading,
  });

  // Handle clicks outside modal to close
  const ref = useClickOutside({
    onClickOutside: toggleOpen,
    open: true,
  });

  return (
    <motion.div
      className={styles.container}
      variants={slideIn}
      initial="hidden"
      animate="visible"
      exit="hidden"
      //@ts-ignore
      ref={ref}
    >
      <ModalHeader title={placeholder} onClose={toggleOpen} />
      <div className={styles.container__content}>
        <DatePickerMobileSelectorColumn
          content={days}
          currentIndex={day + 1}
          onSelectedItem={updateDay}
          type="day"
        />
        <DatePickerMobileSelectorColumn
          content={months}
          currentIndex={month}
          onSelectedItem={updateMonth}
          type="month"
        />
        <DatePickerMobileSelectorColumn
          content={years}
          currentIndex={year}
          onSelectedItem={updateYear}
          type="year"
        />
      </div>
    </motion.div>
  );
}
